import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CreditCard, Loader2, ShoppingCart, Trash2, X } from 'lucide-react';
import axios from 'axios';
import { useCart } from '@/context/CartContext';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { customerApi } from '@/lib/customerApi';
import { formatApiError } from '@/lib/api';
import { readFreightMemory, cartSignature } from '@/lib/freightMemory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (value) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(value || 0));

export const CartDrawer = () => {
  const cart = useCart();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [priced, setPriced] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [freight, setFreight] = useState(null);

  useEffect(() => {
    if (!cart.isOpen) return;
    const saved = readFreightMemory();
    setFreight(saved && saved.signature === cartSignature(cart.lines) ? saved : null);
  }, [cart.isOpen, cart.lines]);

  const price = useCallback(async () => {
    if (!cart.lines.length) { setPriced(null); return; }
    try {
      const { data } = await axios.post(`${API}/account/cart/price`, { lines: cart.lines.map(({ kind, id, quantity }) => ({ kind, id, quantity })) });
      setPriced(data);
      setError('');
    } catch (failure) { setError(formatApiError(failure, 'Could not price this cart')); }
  }, [cart.lines]);

  useEffect(() => { if (cart.isOpen) price(); }, [cart.isOpen, price]);

  useEffect(() => {
    const escape = (event) => { if (event.key === 'Escape') cart.close(); };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [cart]);

  const payByCard = async () => {
    if (!customer) { cart.close(); navigate('/account/login?returnTo=/cart'); return; }
    setBusy(true); setError('');
    try {
      const { data } = await customerApi.post('/account/cart/checkout', { lines: cart.lines.map(({ kind, id, quantity }) => ({ kind, id, quantity })) });
      window.location.href = data.checkout_url;
    } catch (failure) { setError(formatApiError(failure, 'Checkout could not be opened. No payment was taken.')); setBusy(false); }
  };

  if (!cart.isOpen) return null;
  const blocked = Boolean(priced?.lines?.some((line) => !line.card_payable));

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Your cart" data-testid="cart-drawer">
      <button type="button" aria-label="Close cart" onClick={cart.close} className="absolute inset-0 bg-ei-ink/40 backdrop-blur-[2px]" data-testid="cart-drawer-backdrop" />
      <aside className="absolute right-0 top-0 h-full w-full max-w-[400px] border-l border-white/15 bg-ei-navy/80 backdrop-blur-xl text-ei-white shadow-2xl flex flex-col animate-[slideIn_.25s_ease-out]">
        <header className="flex items-center justify-between gap-3 border-b border-white/15 px-5 py-4">
          <p className="font-display text-base font-bold inline-flex items-center gap-2"><ShoppingCart className="h-4 w-4 text-ei-gold" /> Your cart{cart.count ? ` (${cart.count})` : ''}</p>
          <button type="button" onClick={cart.close} aria-label="Close cart" className="inline-flex h-11 w-11 items-center justify-center text-ei-white/70 hover:text-ei-white" data-testid="cart-drawer-close"><X className="h-5 w-5" /></button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!cart.lines.length ? (
            <p className="text-sm text-ei-white/70" data-testid="cart-drawer-empty">Your cart is empty.</p>
          ) : (
            <ul className="space-y-3">
              {cart.lines.map((line) => {
                const row = priced?.lines?.find((item) => item.id === line.id && item.kind === line.kind);
                return (
                  <li key={`${line.kind}-${line.id}`} className="flex items-center gap-3 rounded-sm border border-white/10 bg-white/5 p-3" data-testid={`cart-drawer-line-${line.id}`}>
                    <span className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-sm bg-white/10">
                      {line.image ? <img src={line.image} alt="" className="h-full w-full object-contain" data-testid={`cart-drawer-image-${line.id}`} /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{row?.name || line.name}</span>
                      <span className="block text-xs text-ei-white/60 tnum">{!row ? 'Pricing…' : row.unit_price_ex_gst === null ? 'Quote required' : `${money(row.unit_price_ex_gst)} ex GST`}</span>
                      {Boolean(line.includes?.length) && <span className="mt-1 block text-[11px] leading-snug text-ei-white/50" data-testid={`cart-drawer-includes-${line.id}`}>{line.includes.join(' · ')}</span>}
                      {row && !row.card_payable && <span className="mt-1 block text-xs text-amber-200">{row.reason}</span>}
                    </span>
                    <input type="number" min="1" max="50" value={line.quantity} onChange={(e) => cart.setQuantity(line.id, line.kind, e.target.value)}
                      aria-label={`Quantity for ${line.name}`} className="h-10 w-16 rounded-sm border border-white/25 bg-white/10 px-2 text-sm tnum" data-testid={`cart-drawer-qty-${line.id}`} />
                    <button type="button" onClick={() => cart.remove(line.id, line.kind)} aria-label={`Remove ${line.name}`} className="inline-flex h-10 w-10 items-center justify-center text-ei-white/50 hover:text-red-300" data-testid={`cart-drawer-remove-${line.id}`}><Trash2 className="h-4 w-4" /></button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {Boolean(cart.lines.length) && (
          <footer className="border-t border-white/15 px-5 py-4" data-testid="cart-drawer-pay">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt>Equipment (ex GST)</dt><dd className="tnum" data-testid="cart-drawer-subtotal">{priced ? money(priced.subtotal_ex_gst) : '—'}</dd></div>
              <div className="flex justify-between"><dt>GST {priced?.gst_rate_percent ?? 10}%</dt><dd className="tnum" data-testid="cart-drawer-gst">{priced ? money(priced.gst) : '—'}</dd></div>
              <div className="flex justify-between border-t border-white/15 pt-2 text-base font-bold"><dt>Total</dt><dd className="tnum" data-testid="cart-drawer-total">{priced ? money(priced.total_inc_gst) : '—'}</dd></div>
            </dl>
            <p className="mt-3 text-xs text-ei-white/60">Freight is estimated on the full cart page. Final freight and service availability will be confirmed before dispatch.</p>
            {freight ? (
              <dl className="mt-3 space-y-1 border-t border-white/15 pt-3 text-sm" data-testid="cart-drawer-freight">
                <div className="flex justify-between">
                  <dt>{freight.mode === 'pickup' ? `Pickup — ${freight.postcode}` : `Est. freight to ${freight.postcode}`}</dt>
                  <dd className="tnum" data-testid="cart-drawer-freight-amount">{money(freight.amount_inc_gst)}</dd>
                </div>
                <div className="flex justify-between font-bold">
                  <dt>Est. landed total</dt>
                  <dd className="tnum" data-testid="cart-drawer-landed-total">{priced ? money(priced.total_inc_gst + Number(freight.amount_inc_gst || 0)) : '—'}</dd>
                </div>
                <p className="pt-1 text-xs text-ei-white/55">Estimate for {freight.suburb} {freight.postcode}. Change it on the full cart page.</p>
              </dl>
            ) : (
              <p className="mt-3 border-t border-white/15 pt-3 text-xs text-ei-white/70" data-testid="cart-drawer-freight-prompt">
                Freight not estimated yet — add your postcode on the <Link to="/cart" onClick={cart.close} className="font-bold underline" data-testid="cart-drawer-freight-link">full cart page</Link> to see your landed total.
              </p>
            )}
            {priced?.mode === 'test' && <p className="mt-2 text-xs text-amber-100" data-testid="cart-drawer-test-mode">Preview runs on Stripe test keys. Never enter a real card here.</p>}
            {error && <p className="mt-2 text-xs text-red-200" data-testid="cart-drawer-error">{error}</p>}
            <button type="button" onClick={payByCard} disabled={busy || !priced || blocked}
              className="mt-4 inline-flex w-full min-h-[48px] items-center justify-center gap-2 rounded-sm bg-ei-gold px-5 py-3 font-display text-sm font-bold text-ei-navy hover:bg-[#b89747] disabled:opacity-50"
              data-testid="cart-drawer-pay-by-card">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}{customer ? 'Pay by card' : 'Sign in to pay by card'}
            </button>
            <Link to="/cart" onClick={cart.close} className="mt-2 inline-flex w-full min-h-[44px] items-center justify-center rounded-sm border border-white/30 px-5 py-3 text-sm font-bold text-ei-white hover:bg-white/10" data-testid="cart-drawer-view-cart">View full cart</Link>
            <Link to="/request-a-quote?cart=1" onClick={cart.close} className="mt-3 inline-flex w-full items-center justify-center text-xs font-bold text-ei-white/75 underline hover:text-ei-white" data-testid="cart-drawer-quote-cart">Send the whole cart as a quote request</Link>
          </footer>
        )}
      </aside>
    </div>
  );
};
